<template>
  <div class="view-container">
    <div class="page-card">
      <div class="page-title">
        <i class="fas fa-lock"></i> 修改密码
      </div>

      <div class="form-section">
        <div class="security-tips">
          <div class="tip-item">
            <i class="fas fa-info-circle"></i>
            <div>
              <strong>密码安全建议：</strong>
              <ul>
                <li>建议长度至少8位</li>
                <li>建议包含大写字母、小写字母和数字</li>
                <li>不能与旧密码相同</li>
                <li>定期更换密码以保证安全</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="form-body">
          <div class="form-group">
            <label>当前密码</label>
            <div class="password-input-wrap">
              <input 
                v-model="form.oldPassword" 
                :type="showOld ? 'text' : 'password'" 
                class="form-control" 
                placeholder="请输入当前密码"
                @input="clearError"
              >
              <button class="toggle-btn" @click="showOld = !showOld" type="button">
                <i class="fas" :class="showOld ? 'fa-eye-slash' : 'fa-eye'"></i>
              </button>
            </div>
          </div>

          <div class="form-group">
            <label>新密码</label>
            <div class="password-input-wrap">
              <input 
                v-model="form.newPassword" 
                :type="showNew ? 'text' : 'password'" 
                class="form-control" 
                placeholder="请输入新密码"
                @input="checkStrength"
              >
              <button class="toggle-btn" @click="showNew = !showNew" type="button">
                <i class="fas" :class="showNew ? 'fa-eye-slash' : 'fa-eye'"></i>
              </button>
            </div>
            <div class="strength-bar" v-if="form.newPassword">
              <div class="strength-fill" :class="strengthClass" :style="{ width: strengthPercent + '%' }"></div>
              <span class="strength-text" :class="strengthClass">{{ strengthLabel }}</span>
            </div>
          </div>

          <div class="form-group">
            <label>确认新密码</label>
            <div class="password-input-wrap">
              <input 
                v-model="form.confirmPassword" 
                :type="showConfirm ? 'text' : 'password'" 
                class="form-control" 
                placeholder="请再次输入新密码"
                @input="clearError"
              >
              <button class="toggle-btn" @click="showConfirm = !showConfirm" type="button">
                <i class="fas" :class="showConfirm ? 'fa-eye-slash' : 'fa-eye'"></i>
              </button>
            </div>
            <span v-if="form.confirmPassword && form.newPassword !== form.confirmPassword" class="error-text">
              <i class="fas fa-exclamation-circle"></i> 两次输入的密码不一致
            </span>
          </div>

          <div v-if="errorMsg" class="error-msg">
            <i class="fas fa-exclamation-triangle"></i> {{ errorMsg }}
          </div>

          <div class="form-actions">
            <button @click="handleReset" class="btn btn-default">
              <i class="fas fa-undo"></i> 重置
            </button>
            <button @click="handleSubmit" class="btn btn-primary" :disabled="!canSubmit || submitting">
              <i class="fas" :class="submitting ? 'fa-spinner fa-spin' : 'fa-check'"></i> {{ submitting ? '提交中...' : '确认修改' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { updateUserPassword } from '../api/user'
import { getProfile } from '../api/auth'

const showOld = ref(false)
const showNew = ref(false)
const showConfirm = ref(false)
const errorMsg = ref('')
const strengthPercent = ref(0)
const strengthLabel = ref('')
const strengthClass = ref('')
const submitting = ref(false)
const currentUserGuid = ref('')

const form = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
})

function checkStrength() {
  const pwd = form.newPassword
  if (!pwd) {
    strengthPercent.value = 0
    strengthLabel.value = ''
    strengthClass.value = ''
    return
  }

  let score = 0
  if (pwd.length >= 8) score += 25
  if (pwd.length >= 12) score += 10
  if (/[a-z]/.test(pwd)) score += 15
  if (/[A-Z]/.test(pwd)) score += 20
  if (/[0-9]/.test(pwd)) score += 15
  if (/[^a-zA-Z0-9]/.test(pwd)) score += 15

  strengthPercent.value = Math.min(score, 100)
  if (score < 40) {
    strengthLabel.value = '弱'
    strengthClass.value = 'weak'
  } else if (score < 70) {
    strengthLabel.value = '中'
    strengthClass.value = 'medium'
  } else {
    strengthLabel.value = '强'
    strengthClass.value = 'strong'
  }
}

function clearError() {
  errorMsg.value = ''
}

const canSubmit = computed(() => {
  return form.oldPassword && form.newPassword && form.confirmPassword && 
         form.newPassword === form.confirmPassword
})

function handleReset() {
  form.oldPassword = ''
  form.newPassword = ''
  form.confirmPassword = ''
  errorMsg.value = ''
  strengthPercent.value = 0
  strengthLabel.value = ''
  strengthClass.value = ''
}

async function handleSubmit() {
  if (!form.oldPassword) {
    errorMsg.value = '请输入当前密码'
    return
  }
  if (!form.newPassword) {
    errorMsg.value = '请输入新密码'
    return
  }
  if (form.newPassword === form.oldPassword) {
    errorMsg.value = '新密码不能与当前密码相同'
    return
  }
  if (form.newPassword !== form.confirmPassword) {
    errorMsg.value = '两次输入的密码不一致'
    return
  }

  if (!currentUserGuid.value) {
    errorMsg.value = '无法获取当前用户信息'
    return
  }

  submitting.value = true
  try {
    const res = await updateUserPassword(currentUserGuid.value, form.oldPassword, form.newPassword)
    if (res.resultType || res.ResultType) {
      $message.success('密码修改成功，请牢记新密码！')
      handleReset()
    } else {
      errorMsg.value = res.message || res.Message || res.msg || res.Msg || '密码修改失败'
    }
  } catch (e) {
    console.error('密码修改请求失败:', e)
    const msg = e?.response?.data?.message || e?.response?.data?.Message || e?.message || '修改失败'
    errorMsg.value = msg
  } finally {
    submitting.value = false
  }
}

onMounted(async () => {
  try {
    const res = await getProfile()
    const data = res.data || res.Data || res
    currentUserGuid.value = data.guid || data.Guid || ''
    console.log('当前用户Guid:', currentUserGuid.value)
  } catch (e) {
    console.error('获取用户信息失败', e)
  }
})
</script>

<style scoped>
.view-container {
  padding: 16px;
  background: #f5f7fa;
  min-height: calc(94vh - 110px);
}

.page-card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8ecf1;
  overflow: hidden;
  max-width: 560px;
  margin: 0 auto;
}

.page-title {
  padding: 14px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  border-bottom: 1px solid #e8ecf1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-title i {
  color: #10b981;
}

.form-section {
  padding: 24px;
}

.security-tips {
  background: #f0fdf4;
  border: 1px solid #d1fae5;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 24px;
}

.tip-item {
  display: flex;
  gap: 10px;
  color: #065f46;
  font-size: 12px;
}

.tip-item i {
  font-size: 16px;
  margin-top: 1px;
  flex-shrink: 0;
}

.tip-item strong {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
}

.tip-item ul {
  margin: 0;
  padding-left: 16px;
}

.tip-item li {
  margin-bottom: 3px;
  color: #047857;
}

.form-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.form-group label {
  font-size: 12px;
  font-weight: 500;
  color: #374151;
}

.password-input-wrap {
  position: relative;
  display: flex;
}

.password-input-wrap .form-control {
  width: 100%;
  padding: 10px 38px 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;
}

.password-input-wrap .form-control:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16,185,129,0.1);
}

.toggle-btn {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  border-radius: 3px;
}

.toggle-btn:hover {
  color: #6b7280;
  background: #f3f4f6;
}

.strength-bar {
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  position: relative;
  margin-top: 2px;
  display: flex;
  align-items: center;
}

.strength-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s;
}

.strength-fill.weak { background: #ef4444; }
.strength-fill.medium { background: #f59e0b; }
.strength-fill.strong { background: #10b981; }

.strength-text {
  font-size: 10px;
  margin-left: 8px;
  font-weight: 500;
  white-space: nowrap;
}

.strength-text.weak { color: #ef4444; }
.strength-text.medium { color: #f59e0b; }
.strength-text.strong { color: #10b981; }

.error-text {
  font-size: 11px;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 4px;
}

.error-text i {
  font-size: 11px;
}

.error-msg {
  padding: 10px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.error-msg i {
  font-size: 14px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
}

.btn {
  padding: 9px 20px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.15s;
}

.btn-default {
  background: #fff;
  border: 1px solid #d1d5db;
  color: #374151;
}

.btn-default:hover {
  background: #f9fafb;
}

.btn-primary {
  background: #10b981;
  border: none;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #059669;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>